/**
 * What each Jira auth method asks the admin for.
 *
 * `user: null` means the credential has no user half — the method is
 * bearer-scheme, and any value sitting in the form's `auth_user` is stale.
 *
 * Six things in {@link SourcesAdminPage} used to spell this rule themselves:
 * the zod refinement, the user field's visibility, its label, its placeholder,
 * the secret field's label, and what the submit handler sends. Deriving them
 * all from one table is not tidiness — the secret label had drifted, calling
 * a `basic` password an "API token", precisely because it was written
 * independently of the rule it depends on.
 *
 * This mirrors `AuthMethod::requires_user` in the submodule's `jira-source`
 * crate, deliberately rather than by generation — ts-rs exports types, not
 * methods, and shipcast's frontend types come from the OpenAPI spec anyway.
 * The two are not redundant, they have different jobs: **the backend rejects**
 * (unbypassable — `validate_source_fields` at the write boundary, and
 * `SourceBuilder::build` behind it), **this hides and clears** (an affordance,
 * so the admin is never asked for a field that does nothing).
 *
 * Ported from actionable-metrics' `features/source/wizard.ts`, which reached
 * the same shape first. Kept local rather than promoted to a shared package:
 * the two repos' frontends get their types by different mechanisms (ts-rs vs
 * OpenAPI), so there is no delivery channel — see shipcast-7vs.
 */

import type { TrackerAuthMethod } from './types';

/**
 * The user half of a credential, when the method has one.
 *
 * Label and placeholder live together because they are never independently
 * present: a method either asks for a user (and needs both) or does not (and
 * needs neither). Two correlated nullable fields would let an impossible
 * combination be written; one nullable object cannot.
 */
export interface AuthUserField {
  label: string;
  placeholder: string;
}

export interface AuthMethodShape {
  /** `null` for bearer-scheme methods, whose token carries no user half. */
  user: AuthUserField | null;
  /** What the secret is called to the admin — not always "token". */
  secretLabel: string;
}

export const AUTH_METHOD_SHAPE: Record<TrackerAuthMethod, AuthMethodShape> = {
  api_token: {
    user: { label: 'Atlassian email', placeholder: 'you@example.com' },
    secretLabel: 'API token',
  },
  basic: {
    user: { label: 'Username', placeholder: 'username' },
    secretLabel: 'Password',
  },
  pat: {
    user: null,
    secretLabel: 'Personal Access Token',
  },
};

/** Whether `auth_user` is part of the credential for this method. */
export function authRequiresUser(method: TrackerAuthMethod): boolean {
  return AUTH_METHOD_SHAPE[method].user !== null;
}

/**
 * Whether the form satisfies the required-user rule.
 *
 * Trims, because the backend does: `validate_source_fields` and upstream's
 * `JiraConfig::validate` both reject a whitespace-only user. Without the trim
 * the form would happily submit `"   "` and take a server 400 it could have
 * caught locally.
 */
export function hasRequiredUser(input: { auth_method: TrackerAuthMethod; auth_user?: string | null }): boolean {
  return !authRequiresUser(input.auth_method) || (input.auth_user ?? '').trim() !== '';
}

/**
 * The value to send as `auth_user`.
 *
 * `null` for bearer methods regardless of what is in the form: the user field
 * is hidden on switching to PAT but the form state keeps its last value, so
 * without this a username typed under `basic` would be sealed into the
 * credential store under a method that never reads it.
 */
export function normalizeAuthUser(method: TrackerAuthMethod, raw: string | null | undefined): string | null {
  return authRequiresUser(method) ? (raw ?? '').trim() : null;
}

/**
 * Label for the secret input. Editing is a rotation, so the label says so and
 * notes that leaving it empty keeps the stored credential.
 */
export function secretFieldLabel(method: TrackerAuthMethod, isEditing: boolean): string {
  const { secretLabel } = AUTH_METHOD_SHAPE[method];
  return isEditing ? `New ${secretLabel.toLowerCase()} (leave empty to keep existing)` : secretLabel;
}
